import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import Button from "ui/Button";
import Grid from "ui/Grid";
import { WebhookIcon } from "ui/Icons";
import ModalContainer from "ui/ModalContainer";
import Panel from "ui/Panel";
import PanelSection from "ui/PanelSection";
import { useFeature } from "providers/OrganizationFeatures";
import { useSetFlash, useSetNextFlashLocation } from "providers/WithFlashes";
import { useRoutes } from "utils/routes";
import { useAppConfig } from "providers/WithAppConfig";

import { OrganizationIntegrationsQuery } from "../gql/Query.generated";
import { useFindOrCreateWebhookSigningKeyMutation } from "./gql/Mutation.generated";
import EndpointDetailModal from "./EndpointDetail";
import {
  EndpointHealthSummary,
  EndpointHealthType,
  lastDeliveriesDescription,
} from "./EndpointHealth";
import SigningKeys, { WebhookSigningKeyType } from "./SigningKeys";
import WebhookForm, { currentSigningKey } from "./WebhookForm";

type WebhookEndpointType =
  OrganizationIntegrationsQuery["getOrganizationDetails"]["webhookEndpoints"][number];

type EndpointListItemType = Pick<WebhookEndpointType, "id" | "name" | "url"> &
  EndpointHealthType;

const REFETCH_QUERIES = ["OrganizationIntegrations"];

function WebhooksPanel({
  organizationSlug,
  endpoints,
  signingKeys,
  canEdit,
}: {
  organizationSlug: string;
  endpoints: WebhookEndpointType[];
  signingKeys: WebhookSigningKeyType[];
  canEdit: boolean;
}) {
  const hasWebhooks = useFeature("webhooks");
  const config = useAppConfig();
  const { organizationAlertDestinations, organizationIntegrationsWebhook } =
    useRoutes();
  const [adding, setAdding] = useState(false);
  const setFlash = useSetFlash();
  const { createSigningKeyIfAbsent, error: signingKeyCreationError } =
    useCreateSigningKeyIfAbsent(organizationSlug, signingKeys);
  const { selectedEndpoint, closeEndpoint, endpointRemoved } =
    useSelectedEndpoint(organizationSlug, endpoints, "webhookEndpointId");

  if (!hasWebhooks && endpoints.length === 0) {
    return null;
  }

  if (!config?.webhooksAvailable) {
    return (
      <Panel title="Webhooks">
        <PanelSection>
          <p className="mb-0">
            Webhooks require encrypted credential storage, which is not
            configured for this pganalyze installation. Set{" "}
            <code>ENCRYPTION_PRIMARY_KEY</code> and{" "}
            <code>ENCRYPTION_KEY_DERIVATION_SALT</code> to enable them.
          </p>
        </PanelSection>
      </Panel>
    );
  }

  return (
    <Panel title="Webhooks">
      <PanelSection>
        <p className="mb-0">
          Deliver alerts as signed HTTP requests to your own endpoints. Add an
          endpoint here, then pick it as a target in an{" "}
          <Link to={organizationAlertDestinations(organizationSlug)}>
            alert destination
          </Link>
          .
        </p>
      </PanelSection>
      {endpoints.length > 0 && (
        <EndpointList
          organizationSlug={organizationSlug}
          endpoints={endpoints}
          endpointPath={organizationIntegrationsWebhook}
        />
      )}
      <PanelSection>
        {endpoints.length === 0 && (
          <div className="mb-2 text-[#606771]">
            No webhook endpoints configured yet.
          </div>
        )}
        {canEdit ? (
          <Button
            onClick={() => {
              // So the form can show the secret before its test request is sent
              createSigningKeyIfAbsent();
              setAdding(true);
            }}
          >
            <span className="flex items-center justify-center gap-1.5 px-3">
              <WebhookIcon />
              Add Webhook
            </span>
          </Button>
        ) : (
          <>
            You are not allowed to edit integration settings for this
            organization.
          </>
        )}
      </PanelSection>
      {signingKeys.length > 0 && (
        <SigningKeys
          organizationSlug={organizationSlug}
          keys={signingKeys}
          canEdit={canEdit}
        />
      )}
      {adding && (
        <ModalContainer
          title="Add Webhook"
          layout="centered"
          className="!min-w-0"
          onClose={() => setAdding(false)}
        >
          <WebhookForm
            organizationSlug={organizationSlug}
            signingKeys={signingKeys}
            signingKeyCreationError={signingKeyCreationError}
            onSaved={(message) => {
              setFlash("success", message);
              setAdding(false);
            }}
            onCancel={() => setAdding(false)}
          />
        </ModalContainer>
      )}
      {selectedEndpoint && (
        <EndpointDetailModal
          key={selectedEndpoint.id}
          organizationSlug={organizationSlug}
          endpoint={selectedEndpoint}
          signingKeys={signingKeys}
          canEdit={canEdit}
          onClose={closeEndpoint}
          onRemoved={() =>
            endpointRemoved(`Webhook "${selectedEndpoint.name}" removed`)
          }
        />
      )}
    </Panel>
  );
}

export function EndpointList({
  organizationSlug,
  endpoints,
  endpointPath,
}: {
  organizationSlug: string;
  endpoints: EndpointListItemType[];
  endpointPath: (organizationSlug: string, endpointId: string) => string;
}) {
  return (
    <Grid
      data={endpoints}
      sortParam={false}
      columns={[
        {
          field: "name",
          header: "Name",
          width: "minmax(160px, 1fr)",
          disableSort: true,
          title: true,
          renderer: function NameCell({ rowData }) {
            return (
              <Link to={endpointPath(organizationSlug, rowData.id)}>
                {rowData.name}
              </Link>
            );
          },
        },
        {
          field: "url",
          header: "URL",
          width: "minmax(200px, 1.5fr)",
          style: "query",
          disableSort: true,
          title: true,
        },
        {
          field: "lastSuccessAt",
          header: "Delivery health",
          width: "minmax(320px, 1fr)",
          disableSort: true,
          title: function HealthTitle({ rowData }) {
            return lastDeliveriesDescription(rowData);
          },
          renderer: function HealthCell({ rowData }) {
            return <EndpointHealthSummary endpoint={rowData} />;
          },
        },
      ]}
    />
  );
}

// The endpoint whose modal is open, named by the route parameter
export function useSelectedEndpoint<T extends { id: string }>(
  organizationSlug: string,
  endpoints: T[],
  param: string,
) {
  const endpointId = useParams()[param];
  const navigate = useNavigate();
  const setFlash = useSetFlash();
  const setNextFlashLocation = useSetNextFlashLocation();
  const { organizationIntegrations } = useRoutes();
  const integrationsPath = organizationIntegrations(organizationSlug);

  return {
    selectedEndpoint: endpoints.find((e) => e.id === endpointId),
    closeEndpoint: () => {
      void navigate(integrationsPath);
    },
    endpointRemoved: (message: string) => {
      setNextFlashLocation(integrationsPath);
      setFlash("success", message);
      void navigate(integrationsPath);
    },
  };
}

function useCreateSigningKeyIfAbsent(
  organizationSlug: string,
  signingKeys: WebhookSigningKeyType[],
) {
  const [error, setError] = useState<string | null>(null);
  const [createKey, { loading }] = useFindOrCreateWebhookSigningKeyMutation({
    variables: { organizationSlug },
    refetchQueries: REFETCH_QUERIES,
    awaitRefetchQueries: true,
    onError: (e) => setError(e.message),
  });

  const createSigningKeyIfAbsent = () => {
    if (loading || currentSigningKey(signingKeys)) {
      return;
    }
    setError(null);
    void createKey();
  };

  return { createSigningKeyIfAbsent, error };
}

export default WebhooksPanel;
